import { FC, useEffect, useState, CSSProperties } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { PageTitle } from '../../../_metronic/layout/core'
import { getDashboardStats } from '../../config/api'
import moment from 'moment'
import './DashboardStyles.css'

// Chart components
import { ProjectTrendChart } from './components/ProjectTrendChart'
import { ProjectStatusPieChart } from './components/ProjectStatusPieChart'
import { ProjectsByRoofTypeChart } from './components/ProjectsByRoofTypeChart'
import { WorkforceTodayPanel } from './components/WorkforceTodayPanel'
import { CountUp } from './components/CountUp'

interface DashboardStats {
    projects: {
        total: number
        byStatus: Array<{ _id: string; count: number }>
        nogo: number
        thisMonth: number
        categorized: {
            pricing: number
            draft: number
            upcoming: number
            ongoing: number
            finished: number
        }
    }
    contractors: {
        total: number
        active: number
        verified: number
        pendingVerification: number
    }
    employees: {
        total: number
        assigned: number
    }
    queries: {
        total: number
        unresolved: number
    }
    timesheet: {
        today: {
            clocked_in: number
            on_break: number
            completed: number
            holiday: number
            sick: number
            absent: number
            not_clocked_in: number
        }
        totalEmployees: number
        hoursToday: number
        hoursThisWeek: number
    }
    upcomingBankHoliday?: {
        name: string
        dateStr: string
        daysUntil: number
    }
    projectsByRoofType: Array<{ _id: string; count: number }>
    monthlyTrend: Array<{ _id: { year: number; month: number }; count: number }>
    recentProjects: Array<any>
    pendingVerifications: Array<any>
}

const dublinTime = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat('en-IE', { timeZone: 'Europe/Dublin', ...opts })

const DashboardPage: FC = () => {
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

    useEffect(() => {
        fetchDashboardStats()
        const interval = setInterval(() => fetchDashboardStats(), 300000)
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchDashboardStats = async (manual = false) => {
        try {
            setError(null)
            if (manual) setRefreshing(true)
            const response = await getDashboardStats()
            if (response.status) {
                setStats(response.data)
                setLastUpdated(new Date())
            } else {
                setError('Failed to load dashboard data')
            }
        } catch (err: any) {
            console.error('Dashboard fetch error:', err)
            setError(err.message || 'Failed to load dashboard data')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    const getStatusLabel = (status: string): string => {
        const labels: Record<string, string> = {
            pricing: 'Pricing',
            create: 'Draft',
            draft: 'Draft',
            priced: 'Priced',
            published: 'Published',
            ongoing: 'Ongoing',
            finished: 'Finished',
        }
        return labels[status] || status
    }

    // ---- Greeting strip helpers (Europe/Dublin) ----
    const renderHero = () => {
        const nowD = new Date()
        const hour = Number(dublinTime({ hour: 'numeric', hour12: false }).format(nowD))
        const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'
        const dateStr = dublinTime({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(nowD)

        const bh = stats?.upcomingBankHoliday
        let bhDateLabel = ''
        if (bh) {
            const [by, bm, bd] = bh.dateStr.split('-').map(Number)
            const bhDate = new Date(by, bm - 1, bd)
            const dateFmt = new Intl.DateTimeFormat('en-IE', { weekday: 'short', day: 'numeric', month: 'short' }).format(bhDate)
            const rel = bh.daysUntil <= 0 ? 'Today' : bh.daysUntil === 1 ? 'Tomorrow' : `in ${bh.daysUntil} days`
            bhDateLabel = `${dateFmt} · ${rel}`
        }

        return (
            <div className="gc-hero gc-reveal">
                <div className="gc-hero-left">
                    <div className="gc-hero-eyebrow">
                        <span className="gc-live-dot"></span> Operations · Live
                    </div>
                    <h1 className="gc-hero-title">{greeting}</h1>
                    <div className="gc-hero-date">{dateStr}</div>
                </div>
                {bh && (
                    <div className="gc-hero-center">
                        <div className="gc-hero-center-eyebrow">
                            <i className="bi bi-calendar-event"></i> Next bank holiday
                        </div>
                        <div className="gc-hero-center-name">{bh.name}</div>
                        <div className="gc-hero-center-date">{bhDateLabel}</div>
                    </div>
                )}
                <div className="gc-hero-right">
                    <div className="gc-hero-sync">
                        {lastUpdated ? (
                            <>Updated <b>{dublinTime({ hour: '2-digit', minute: '2-digit', hour12: false }).format(lastUpdated)}</b></>
                        ) : (
                            <>Syncing…</>
                        )}
                        <br />Auto-refresh 5 min
                    </div>
                    <button
                        className={`gc-refresh-btn ${refreshing ? 'is-spinning' : ''}`}
                        onClick={() => fetchDashboardStats(true)}
                        disabled={refreshing}
                        title="Refresh now"
                    >
                        <i className="bi bi-arrow-clockwise"></i> Refresh
                    </button>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="gc-dashboard">
                <div className="gc-hero gc-skel" style={{ height: 116, marginBottom: '1.75rem' }}></div>
                <div className="gc-section-header">Overview</div>
                <div className="gc-kpi-grid">
                    {[0, 1, 2, 3].map((i) => (
                        <div className="gc-skel-card" key={i}>
                            <div className="gc-skel gc-skel-line" style={{ width: 46, height: 46, borderRadius: 13 }}></div>
                            <div className="gc-skel gc-skel-line" style={{ width: '55%', height: 34, marginTop: 18 }}></div>
                            <div className="gc-skel gc-skel-line" style={{ width: '70%' }}></div>
                        </div>
                    ))}
                </div>
                <div className="gc-skel-card" style={{ height: 150, marginBottom: '2rem' }}></div>
                <div className="gc-section-header">Project Status</div>
                <div className="gc-status-grid">
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                        <div className="gc-skel-card" key={i} style={{ height: 74, padding: '1.1rem' }}></div>
                    ))}
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="gc-dashboard">
                <div className="gc-error">
                    <div className="gc-error-icon">
                        <i className="bi bi-exclamation-triangle"></i>
                    </div>
                    <div className="gc-error-content">
                        <h4 className="gc-error-title">Error Loading Dashboard</h4>
                        <p className="gc-error-message">{error}</p>
                    </div>
                    <button className="gc-error-action" onClick={() => fetchDashboardStats(true)}>
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    if (!stats) {
        return (
            <div className="gc-dashboard">
                <div className="gc-error">
                    <div className="gc-error-icon">
                        <i className="bi bi-inbox"></i>
                    </div>
                    <div className="gc-error-content">
                        <h4 className="gc-error-title">No Data Available</h4>
                        <p className="gc-error-message">Dashboard data could not be loaded</p>
                    </div>
                </div>
            </div>
        )
    }

    const cz = stats.projects.categorized || { pricing: 0, draft: 0, upcoming: 0, ongoing: 0, finished: 0 }
    const statusCards: Array<{ to: string; icon: string; cls: string; label: string; value: number; c: string }> = [
        { to: '/admin/project/AllProject/all?filter=pricing', icon: 'bi-cash-stack', cls: 'pricing', label: 'Pricing', value: cz.pricing, c: '#9333ea' },
        { to: '/admin/project/AllProject/all?filter=draft', icon: 'bi-pencil-square', cls: 'draft', label: 'Draft', value: cz.draft, c: '#64748b' },
        { to: '/admin/project/AllProject/all?filter=upcoming', icon: 'bi-calendar-event', cls: 'upcoming', label: 'Upcoming', value: cz.upcoming, c: '#0284c7' },
        { to: '/admin/project/AllProject/all?filter=ongoing', icon: 'bi-play-circle', cls: 'ongoing', label: 'Ongoing', value: cz.ongoing, c: '#d97706' },
        { to: '/admin/project/AllProject/all?filter=finished', icon: 'bi-check-circle', cls: 'finished', label: 'Finished', value: cz.finished, c: '#059669' },
        { to: '/admin/project/AllProject/all?show_nogo=true', icon: 'bi-x-circle', cls: 'nogo', label: 'No-Go', value: stats.projects.nogo, c: '#dc2626' },
    ]

    return (
        <div className="gc-dashboard">
            {renderHero()}

            {/* KPI Cards */}
            <div className="gc-section-header">Overview</div>
            <div className="gc-kpi-grid">
                <Link to="/admin/project/AllProject/all" className="gc-kpi-card gc-kpi-card--primary">
                    <div className="gc-kpi-top">
                        <div className="gc-kpi-icon gc-kpi-icon--primary">
                            <i className="bi bi-folder2-open"></i>
                        </div>
                        {stats.projects.thisMonth > 0 && (
                            <div className="gc-kpi-badge gc-kpi-badge--up">
                                <i className="bi bi-arrow-up-short"></i>
                                <CountUp value={stats.projects.thisMonth} /> this month
                            </div>
                        )}
                    </div>
                    <div className="gc-kpi-value gc-num"><CountUp value={stats.projects.total} /></div>
                    <div className="gc-kpi-label">Total Projects</div>
                    <div className="gc-kpi-sublabel">excluding {stats.projects.nogo} no-go</div>
                </Link>

                <Link to="/admin/contractor/AllContractor" className="gc-kpi-card gc-kpi-card--success">
                    <div className="gc-kpi-top">
                        <div className="gc-kpi-icon gc-kpi-icon--success">
                            <i className="bi bi-building"></i>
                        </div>
                        {stats.contractors.pendingVerification > 0 && (
                            <div className="gc-kpi-badge gc-kpi-badge--neutral">
                                {stats.contractors.pendingVerification} pending
                            </div>
                        )}
                    </div>
                    <div className="gc-kpi-value gc-num"><CountUp value={stats.contractors.active} /></div>
                    <div className="gc-kpi-label">Active Contractors</div>
                    <div className="gc-kpi-sublabel">of {stats.contractors.total} total</div>
                </Link>

                <Link to="/admin/employee/allEmployees" className="gc-kpi-card gc-kpi-card--info">
                    <div className="gc-kpi-top">
                        <div className="gc-kpi-icon gc-kpi-icon--info">
                            <i className="bi bi-people"></i>
                        </div>
                    </div>
                    <div className="gc-kpi-value gc-num"><CountUp value={stats.employees.assigned} /></div>
                    <div className="gc-kpi-label">Assigned Employees</div>
                    <div className="gc-kpi-sublabel">of {stats.employees.total} total</div>
                </Link>

                <Link to="/admin/timesheet/whos-in" className="gc-kpi-card gc-kpi-card--live">
                    <div className="gc-kpi-top">
                        <div className="gc-kpi-icon gc-kpi-icon--live">
                            <i className="bi bi-person-workspace"></i>
                        </div>
                        <div className="gc-kpi-badge gc-kpi-badge--live">
                            <span className="gc-live-dot"></span> live
                        </div>
                    </div>
                    <div className="gc-kpi-value gc-num"><CountUp value={stats.timesheet.today.clocked_in} /></div>
                    <div className="gc-kpi-label">Clocked In Now</div>
                    <div className="gc-kpi-sublabel">{stats.timesheet.today.on_break} on break · {stats.timesheet.totalEmployees} workers</div>
                </Link>
            </div>

            {/* Workforce Today */}
            <div className="gc-section-header">Workforce</div>
            <WorkforceTodayPanel data={stats.timesheet} />

            {/* Status Cards */}
            <div className="gc-section-header">Project Status</div>
            <div className="gc-status-grid">
                {statusCards.map((card) => (
                    <Link to={card.to} className="gc-status-card" key={card.cls} style={{ ['--gc-c']: card.c } as CSSProperties}>
                        <div className={`gc-status-icon gc-status-icon--${card.cls}`}>
                            <i className={`bi ${card.icon}`}></i>
                        </div>
                        <div className="gc-status-content">
                            <div className="gc-status-value gc-num"><CountUp value={card.value} /></div>
                            <div className="gc-status-label">{card.label}</div>
                        </div>
                    </Link>
                ))}
            </div>

            {/* Charts Row */}
            <div className="gc-section-header">Analytics</div>
            <div className="row g-4 mb-4 gc-reveal">
                <div className="col-xl-4">
                    <div className="gc-chart-card">
                        <div className="gc-chart-header">
                            <h3 className="gc-chart-title">Status Distribution</h3>
                            <p className="gc-chart-subtitle">Projects by current category</p>
                        </div>
                        <div className="gc-chart-body">
                            <ProjectStatusPieChart categorized={cz} />
                        </div>
                    </div>
                </div>
                <div className="col-xl-8">
                    <div className="gc-chart-card">
                        <div className="gc-chart-header">
                            <h3 className="gc-chart-title">Project Trend</h3>
                            <p className="gc-chart-subtitle">Monthly project creation over time</p>
                        </div>
                        <div className="gc-chart-body">
                            <ProjectTrendChart monthlyTrend={stats.monthlyTrend} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Roof Types Chart */}
            <div className="row g-4 mb-4 gc-reveal">
                <div className="col-12">
                    <div className="gc-chart-card">
                        <div className="gc-chart-header">
                            <h3 className="gc-chart-title">Projects by Roof Type</h3>
                            <p className="gc-chart-subtitle">Distribution across different roof categories</p>
                        </div>
                        <div className="gc-chart-body">
                            <ProjectsByRoofTypeChart projectsByRoofType={stats.projectsByRoofType} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Projects & Pending Verifications */}
            <div className="row g-4 gc-reveal">
                <div className="col-xl-8">
                    <div className="gc-table-card">
                        <div className="gc-table-header">
                            <div>
                                <h3 className="gc-table-title">Recent Projects</h3>
                                <p className="gc-table-subtitle">Latest projects added to the system</p>
                            </div>
                            <Link to="/admin/project/AllProject/all" className="gc-table-action">
                                View All
                            </Link>
                        </div>
                        <table className="gc-table">
                            <thead>
                                <tr>
                                    <th>Project</th>
                                    <th>Contractor</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.recentProjects.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: 'var(--gc-slate-400)' }}>
                                            No projects found
                                        </td>
                                    </tr>
                                ) : (
                                    stats.recentProjects.map((project) => (
                                        <tr key={project._id}>
                                            <td>
                                                <Link to={`/admin/project/ProjectDetails/${project._id}`} className="gc-table-project-name">
                                                    {project.project_name || 'Untitled Project'}
                                                </Link>
                                                <span className="gc-table-project-id">{project.project_id}</span>
                                            </td>
                                            <td>
                                                <span className="gc-table-contractor">
                                                    {project.contractor_id?.company_name || 'Unassigned'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`gc-badge gc-badge--${project.project_status === 'create' ? 'draft' : project.project_status}`}>
                                                    {getStatusLabel(project.project_status)}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <span className="gc-table-date">{moment(project.createdAt).fromNow()}</span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="col-xl-4">
                    <div className="gc-list-card">
                        <div className="gc-list-header">
                            <h3 className="gc-list-title">Pending Verification</h3>
                            <p className="gc-list-subtitle">Contractors awaiting approval</p>
                        </div>
                        <div className="gc-list-body">
                            {stats.pendingVerifications.length === 0 ? (
                                <div className="gc-list-empty">
                                    <div className="gc-list-empty-icon">
                                        <i className="bi bi-check-lg"></i>
                                    </div>
                                    <p className="gc-list-empty-text">All contractors verified</p>
                                </div>
                            ) : (
                                stats.pendingVerifications.map((contractor) => (
                                    <Link key={contractor._id} to={`/admin/contractor/ContractorDetails/${contractor._id}`} className="gc-list-item">
                                        <div className="gc-list-avatar">
                                            <i className="bi bi-person"></i>
                                        </div>
                                        <div className="gc-list-content">
                                            <span className="gc-list-name">
                                                {contractor.company_name || contractor.full_name || 'Unknown'}
                                            </span>
                                            <span className="gc-list-meta">{contractor.email || contractor.contractor_id}</span>
                                        </div>
                                        <span className="gc-list-time">{moment(contractor.createdAt).fromNow()}</span>
                                    </Link>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const DashboardWrapper: FC = () => {
    const intl = useIntl()
    return (
        <>
            <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
            <DashboardPage />
        </>
    )
}

export { DashboardWrapper }
